/**
 * SaveManager — the first-class persistence orchestrator.
 *
 * Ties together the pure (de)serialization (save.ts / meta.ts) and a storage
 * backend (storage.ts) into the API the renderer + bot actually call:
 *
 *   - RUN SLOT: a single active run, autosaved at safe points so quitting and
 *     reopening resumes the EXACT mid-combat state (bit-identical). Loading is
 *     fail-safe: a corrupt/partial/foreign blob never throws — it is reported
 *     and the slot can be cleanly reset.
 *   - META SLOT: account-level progression, persisted independently so it
 *     survives across runs (and survives a corrupt run save).
 *
 * Everything here is deterministic and side-effect-isolated to the injected
 * KVStorage, so it is fully unit-testable with a MemoryStorage.
 */

import {
  serializeRun,
  deserializeRun,
  exportSave,
  importSave,
  type LoadError,
} from "./save";
import {
  serializeMeta,
  deserializeMeta,
  newMeta,
  type MetaState,
} from "./meta";
import type { KVStorage } from "./storage";
import { defaultStorage } from "./storage";
import type { RunState } from "./types";

/** Storage keys. Namespaced so they never collide with other site data. */
export const RUN_SLOT_KEY = "nullrun:run";
export const META_SLOT_KEY = "nullrun:meta";

export interface LoadRunOutcome {
  /** The resumed run, or null if there is no (valid) saved run. */
  run: RunState | null;
  /**
   * Why there is no run, when `run` is null:
   *  - "none": the slot is empty (fresh start; not an error).
   *  - a LoadError: the slot held data we couldn't safely load. The slot was
   *    auto-cleared so the next save starts clean (fail-safe reset).
   */
  status: "ok" | "none" | LoadError;
  /** If the loaded save was migrated up, the version it came from. */
  migratedFrom?: number;
}

export interface LoadMetaOutcome {
  meta: MetaState;
  /**
   *  - "ok": loaded an existing meta save.
   *  - "new": no meta existed; a fresh one was created (and persisted).
   *  - a string error: existing meta was unreadable; it was reset to a fresh
   *    one (which is now persisted) so progression can continue.
   */
  status: "ok" | "new" | string;
  migratedFrom?: number;
}

export class SaveManager {
  constructor(private readonly storage: KVStorage = defaultStorage()) {}

  // --- Run slot ------------------------------------------------------------

  /**
   * Persist the active run. This is the AUTOSAVE entry point: call it at safe
   * points (after each card play, end-turn, node choice, reward) so a quit
   * resumes exactly where the player left off. Returns true on success; a
   * storage failure (e.g. quota) is swallowed and returns false so the game
   * loop never crashes on a failed write.
   */
  saveRun(run: RunState): boolean {
    try {
      this.storage.set(RUN_SLOT_KEY, serializeRun(run));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Load the active run, fail-safe. NEVER throws.
   *  - empty slot -> { run: null, status: "none" }.
   *  - valid -> { run, status: "ok", migratedFrom? }.
   *  - corrupt/partial/foreign/unsupported -> the slot is CLEARED and we return
   *    { run: null, status: <error> } so the UI can offer a clean fresh start.
   */
  loadRun(): LoadRunOutcome {
    let raw: string | null;
    try {
      raw = this.storage.get(RUN_SLOT_KEY);
    } catch {
      raw = null;
    }
    if (raw == null || raw.length === 0) {
      return { run: null, status: "none" };
    }
    const res = deserializeRun(raw);
    if (!res.ok) {
      // Fail safe: drop the unreadable blob so we don't keep tripping on it.
      this.clearRun();
      return { run: null, status: res.error };
    }
    return res.migratedFrom != null
      ? { run: res.state, status: "ok", migratedFrom: res.migratedFrom }
      : { run: res.state, status: "ok" };
  }

  /** True if a run slot is present (regardless of validity). */
  hasRun(): boolean {
    try {
      const raw = this.storage.get(RUN_SLOT_KEY);
      return raw != null && raw.length > 0;
    } catch {
      return false;
    }
  }

  /** Remove the active run (e.g. on death/victory, or fail-safe reset). */
  clearRun(): void {
    this.storage.remove(RUN_SLOT_KEY);
  }

  // --- Meta slot -----------------------------------------------------------

  /** Persist meta-progression. Returns false on a storage failure. */
  saveMeta(meta: MetaState): boolean {
    try {
      this.storage.set(META_SLOT_KEY, serializeMeta(meta));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Load meta-progression, fail-safe. NEVER throws. If absent OR unreadable, a
   * fresh meta is created AND persisted, so progression always has a valid home
   * (a corrupt run save can never wipe out unlocks). The status distinguishes
   * the cases for telemetry/UI.
   */
  loadMeta(): LoadMetaOutcome {
    let raw: string | null;
    try {
      raw = this.storage.get(META_SLOT_KEY);
    } catch {
      raw = null;
    }
    if (raw == null || raw.length === 0) {
      const meta = newMeta();
      this.saveMeta(meta);
      return { meta, status: "new" };
    }
    const res = deserializeMeta(raw);
    if (!res.ok) {
      const meta = newMeta();
      this.saveMeta(meta);
      return { meta, status: res.error };
    }
    // Re-persist after a migration so the upgraded shape is what's on disk.
    if (res.migratedFrom != null) this.saveMeta(res.meta);
    return res.migratedFrom != null
      ? { meta: res.meta, status: "ok", migratedFrom: res.migratedFrom }
      : { meta: res.meta, status: "ok" };
  }

  /** Reset meta to a fresh state (and persist). Used by a "wipe progress" UX. */
  resetMeta(): MetaState {
    const meta = newMeta();
    this.saveMeta(meta);
    return meta;
  }

  // --- Export / import (portable share strings) ----------------------------

  /** Export the active run as a portable base64 string (null if none valid). */
  exportRun(): string | null {
    const { run } = this.loadRun();
    return run ? exportSave(run) : null;
  }

  /** Export an arbitrary run (e.g. for "share this seed/state"). */
  exportRunState(run: RunState): string {
    return exportSave(run);
  }

  /**
   * Import a run from an exported string and make it the active run. Returns the
   * run on success or null on any failure (fail-safe; the existing slot is left
   * untouched on failure).
   */
  importRun(encoded: string): RunState | null {
    const res = importSave(encoded);
    if (!res.ok) return null;
    this.saveRun(res.state);
    return res.state;
  }
}
